(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function gallerySettings() {
    const width = window.innerWidth;
    if (width <= 749) return { target: 170, minItems: 1, maxItems: 2, minHeight: 145, maxHeight: 230 };
    if (width <= 989) return { target: 200, minItems: 2, maxItems: 3, minHeight: 160, maxHeight: 260 };
    if (width <= 1199) return { target: 215, minItems: 2, maxItems: 4, minHeight: 170, maxHeight: 275 };
    return { target: 220, minItems: 2, maxItems: 5, minHeight: 175, maxHeight: 285 };
  }

  function partitionRows(ratios, availableWidth, gap, settings) {
    const n = ratios.length;
    if (!n) return [];
    if (n === 1) return [[0, 1]];

    const dp = Array(n + 1).fill(null);
    dp[n] = { cost: 0, rows: [] };

    for (let i = n - 1; i >= 0; i -= 1) {
      let ratioSum = 0;

      for (let count = 1; count <= settings.maxItems && i + count <= n; count += 1) {
        ratioSum += ratios[i + count - 1];
        const remaining = n - (i + count);

        // Avoid stranded single cards on desktop/tablet when another partition is possible.
        if (remaining === 1 && n > 2 && settings.minItems > 1) continue;
        if (count < settings.minItems && remaining > 0) continue;
        if (!dp[i + count]) continue;

        const rowWidth = availableWidth - gap * (count - 1);
        const height = rowWidth / ratioSum;
        const deviation = (height - settings.target) / settings.target;
        let cost = deviation * deviation * 100;

        // Strongly discourage rows that become visually too short or too tall.
        if (height < settings.minHeight) cost += Math.pow((settings.minHeight - height) / 10, 2) * 30;
        if (height > settings.maxHeight) cost += Math.pow((height - settings.maxHeight) / 10, 2) * 30;

        // A small preference for fuller rows keeps the page compact.
        cost += (settings.maxItems - count) * 0.35;
        cost += dp[i + count].cost;

        if (!dp[i] || cost < dp[i].cost) {
          dp[i] = {
            cost,
            rows: [[i, i + count], ...dp[i + count].rows]
          };
        }
      }
    }

    return dp[0] ? dp[0].rows : [[0, n]];
  }

  function justifyGallery(grid) {
    const cards = Array.from(grid.querySelectorAll('[data-art-card]'));
    if (!cards.length) return;

    // Clear previous inline measurements before measuring the container.
    cards.forEach((card) => {
      card.style.width = '';
      card.style.height = '';
    });

    const availableWidth = grid.clientWidth;
    if (!availableWidth) return;

    const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap) || 10;
    const settings = gallerySettings();
    const ratios = cards.map((card) => {
      const parsed = parseFloat(card.dataset.artRatio);
      return Number.isFinite(parsed) && parsed > 0 ? clamp(parsed, 0.55, 4.5) : 1.3333;
    });

    const rows = partitionRows(ratios, availableWidth, gap, settings);

    rows.forEach(([start, end]) => {
      const rowCards = cards.slice(start, end);
      const rowRatios = ratios.slice(start, end);
      const ratioSum = rowRatios.reduce((sum, ratio) => sum + ratio, 0);
      const usableWidth = availableWidth - gap * (rowCards.length - 1);
      const exactHeight = usableWidth / ratioSum;

      // The DP keeps this close to the target. Do not clamp here: exact height is
      // what makes the artwork itself fill the row with zero cropping/dead space.
      const rowHeight = exactHeight;
      let usedWidth = 0;

      rowCards.forEach((card, index) => {
        let cardWidth;
        if (index === rowCards.length - 1) {
          cardWidth = usableWidth - usedWidth;
        } else {
          cardWidth = Math.round(rowHeight * rowRatios[index] * 10) / 10;
          usedWidth += cardWidth;
        }

        card.style.width = `${Math.max(1, cardWidth)}px`;
        card.style.height = `${Math.max(1, rowHeight)}px`;
      });
    });

    grid.classList.add('is-justified');
  }

  function resetHoverVideo(card) {
    const video = card.querySelector('[data-art-hover-video]');
    if (!video) return;
    video.pause();
    try { video.currentTime = 0; } catch (error) {}
    card.classList.remove('is-video-playing');
  }

  function playHoverVideo(card) {
    const video = card.querySelector('[data-art-hover-video]');
    if (!video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    try { video.currentTime = 0; } catch (error) {}
    card.classList.add('is-video-playing');
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => card.classList.remove('is-video-playing'));
    }
  }

  function initHoverVideos(section) {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    section.querySelectorAll('.ac-art-card.has-hover-video').forEach((card) => {
      if (card.dataset.videoBound === 'true') return;
      card.dataset.videoBound = 'true';

      const video = card.querySelector('[data-art-hover-video]');
      if (!video) return;

      if (canHover) {
        card.addEventListener('pointerenter', () => playHoverVideo(card));
        card.addEventListener('pointerleave', () => resetHoverVideo(card));
      }

      card.addEventListener('focusin', () => {
        if (canHover) playHoverVideo(card);
      });

      card.addEventListener('focusout', (event) => {
        if (!card.contains(event.relatedTarget)) resetHoverVideo(card);
      });
    });
  }

  function initCollection(section) {
    const form = section.querySelector('[data-art-filters]');
    const toggle = section.querySelector('[data-filter-toggle]');
    const groups = section.querySelector('[data-filter-groups]');
    const grid = section.querySelector('.ac-art-grid');

    if (toggle && groups && !toggle.dataset.bound) {
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        groups.classList.toggle('is-open', !isOpen);
      });
    }

    if (form && !form.dataset.bound) {
      form.dataset.bound = 'true';
      form.querySelectorAll('[data-auto-submit]').forEach((control) => {
        control.addEventListener('change', () => form.submit());
      });
    }

    if (grid) justifyGallery(grid);
    initHoverVideos(section);
  }

  function initAll() {
    document.querySelectorAll('[data-art-collection]').forEach(initCollection);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll('.ac-art-grid').forEach(justifyGallery);
    }, 120);
  });

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
})();
